<script setup lang="ts">
/**
 * The one tooltip in the product.
 *
 * Before this, every hint in the portal was a native `title=""`: styled by the operating system, ~1s before
 * it appears, no keyboard access, and invisible to anyone driving by touch. A console whose whole job is
 * observability (BG §22) should not hide its explanations in browser chrome. This wraps the shadcn-vue /
 * Reka tooltip, branded in `ui/tooltip/TooltipContent.vue`, behind one component so a hint is one prop.
 *
 *     <ZhTooltip text="Search — ⌘K / Ctrl+K">
 *         <Button aria-label="Search"><Search /></Button>
 *     </ZhTooltip>
 *
 * The default slot is the trigger and must be a **single element** — it is rendered `as-child`, so the
 * tooltip adds no wrapper of its own and can sit on an `<svg>` node in an execution graph as readily as on
 * a button. Wrap bare text in a `<span>`. Pass `text`, or the `content` slot for something richer.
 *
 * With no text and no content slot it renders the trigger alone, so a hint that is only sometimes known
 * (`:text="row.blockedReason"`) needs no `v-if` at the call site.
 *
 * It carries its own `TooltipProvider`. Reka's `TooltipRoot` *throws* without one, and these sit in
 * components that get mounted on their own in unit tests; a provider per tooltip costs a `provide()` and
 * means a hint can never be the reason a test blows up. Nesting inside the app's `SidebarProvider`
 * (which brings its own) is harmless, and with the default zero delay the two behave identically.
 */
import type { TooltipContentProps } from 'reka-ui'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip'

withDefaults(
    defineProps<{
        /** The hint. Omit it (and the `content` slot) to render the trigger with no tooltip at all. */
        text?: string | null
        side?: TooltipContentProps['side']
        align?: TooltipContentProps['align']
        /** Milliseconds of hover before it shows. Zero is the console default: fast, per BG §22. */
        delayDuration?: number
    }>(),
    { text: null, side: 'top', align: 'center', delayDuration: 0 },
)

const slots = defineSlots<{
    default: () => unknown
    /** Richer than a string — a key/value line, a shortcut list. Takes precedence over `text`. */
    content?: () => unknown
}>()
</script>

<template>
    <slot v-if="!text && !slots.content" />
    <TooltipProvider v-else :delay-duration="delayDuration">
        <Tooltip>
            <TooltipTrigger as-child>
                <slot />
            </TooltipTrigger>
            <TooltipContent :side="side" :align="align">
                <slot name="content">{{ text }}</slot>
            </TooltipContent>
        </Tooltip>
    </TooltipProvider>
</template>
